import { helpAnchor } from '@pages/help/model/anchors';
import { Suspense, createElement, lazy, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate as useNavigateHook } from 'react-router-dom';
import {
  SidebarContainer,
  SidebarHeader,
  FarmSelector,
  FarmIcon,
  FarmName,
  FarmChevron,
  SidebarContent,
  NavSection,
  SectionTitle,
  NavItem,
  NavItemIcon,
  NavItemLabel,
  SidebarFooter,
  CollapseButton,
  SidebarOverlay,
} from './style';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiEdit, FiLock } from 'react-icons/fi';
import { GiFarmTractor } from 'react-icons/gi';
import { ALL_FARMS, FarmContext } from '@context/FarmContext';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined';
import { AuthContext } from '@context/AuthContext';
import { AreaSwitch } from '@components/navigation/AreaSwitch';
import { navigationItems, navigationSections } from '@components/navigation/config';
import {
  getNavigationArea,
  getSectionsForArea,
  getVisibleNavigationItems,
  groupNavigationItems,
  isNavigationItemActive,
} from '@components/navigation/utils';
// Só montados depois do primeiro clique no menu: parados aqui, arrastavam para
// a entrada a lista de municípios do Brasil (81 KB) e o `react-select`, que
// nenhuma tela precisa para pintar.
const MyProfileModal = lazy(() => import('@pages/config/users/modals/myProfile'));
const PasswordModal = lazy(() => import('@pages/config/users/modals/password'));

interface LeftSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen: boolean;
  onMobileClose: () => void;
  onNavigate: () => void;
}

export function LeftSidebar({
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onMobileClose,
  onNavigate,
}: LeftSidebarProps) {
  const navigate = useNavigateHook();
  const location = useLocation();
  const { farm, farms, farmsLoading, farmsError, selectFarm, financialScope, selectFinancialScope, canSelectAllFarms, licenseMode, licenseFeatures } = useContext(FarmContext);
  const { isAuthenticated, signOut, user } = useContext(AuthContext);
  const [isFarmMenuOpen, setIsFarmMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  // Uma vez aberto, o modal fica montado — assim o fechamento se comporta
  // exatamente como antes, inclusive a animação de saída.
  const [profileCarregado, setProfileCarregado] = useState(false);
  const [senhaCarregada, setSenhaCarregada] = useState(false);

  const selectedFarm = farms?.find((entry) => entry.id === farm);
  const isVisuallyCollapsed = isCollapsed && !isMobileOpen;

  const visibleNavigationItems = useMemo(
    () =>
      getVisibleNavigationItems(navigationItems, {
        isAuthenticated,
        user,
        farm,
        licenseMode,
        licenseFeatures,
      }),
    [farm, isAuthenticated, licenseMode, licenseFeatures, user],
  );

  const navigationArea = getNavigationArea(location.pathname);
  const isFinancialArea = navigationArea === 'financial';
  const allSelected = isFinancialArea && financialScope === ALL_FARMS;
  const selectionName = farmsLoading ? 'Carregando fazendas…' : allSelected ? 'Todas as fazendas' : selectedFarm?.name ?? 'Selecione uma fazenda';

  const groupedItems = useMemo(
    () => groupNavigationItems(visibleNavigationItems, getSectionsForArea(navigationSections, navigationArea)),
    [navigationArea, visibleNavigationItems],
  );

  const handleFarmSelect = useCallback(
    (farmId: string) => {
      if (isFinancialArea) selectFinancialScope(farmId);
      else selectFarm(farmId);
      setIsFarmMenuOpen(false);
      onMobileClose();
    },
    [isFinancialArea, onMobileClose, selectFarm, selectFinancialScope],
  );

  const handleSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  const handleNavClick = useCallback(
    (route: string) => {
      navigate(route);
      onNavigate();
    },
    [navigate, onNavigate],
  );

  return (
    <>
      <SidebarOverlay data-open={isMobileOpen} onClick={onMobileClose} aria-hidden={!isMobileOpen} />
      <SidebarContainer data-collapsed={isVisuallyCollapsed} data-mobile-open={isMobileOpen}>
        <SidebarHeader>
          <DropdownMenu.Root open={isFarmMenuOpen} onOpenChange={setIsFarmMenuOpen}>
            <DropdownMenu.Trigger asChild>
              <FarmSelector {...helpAnchor('shell.fazenda')} aria-label={`Fazenda: ${selectionName}`} title={selectionName}>
                <FarmIcon>
                  <GiFarmTractor />
                </FarmIcon>
                {!isVisuallyCollapsed && (
                  <>
                    <FarmName>{selectionName}</FarmName>
                    <FarmChevron>
                      <FiChevronDown />
                    </FarmChevron>
                  </>
                )}
              </FarmSelector>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="start"
                sideOffset={8}
                style={{
                  minWidth: '240px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '8px',
                  boxShadow: '0 4px 16px rgba(26, 77, 58, 0.2)',
                  zIndex: 1302,
                  maxWidth: 'calc(100vw - 24px)',
                }}
              >
                {isFinancialArea && canSelectAllFarms && Boolean(farms?.length) ? (
                  <DropdownMenu.Item
                    onSelect={() => { handleFarmSelect(ALL_FARMS); }}
                    style={{ padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', color: '#1A4D3A',
                      backgroundColor: allSelected ? 'rgba(26, 77, 58, 0.1)' : 'transparent', fontWeight: allSelected ? 600 : 400 }}
                  >Todas as fazendas</DropdownMenu.Item>
                ) : null}
                {farms && farms.length > 0 ? (
                  farms.map((entry) => (
                    <DropdownMenu.Item
                      key={entry.id}
                      onSelect={() => { handleFarmSelect(entry.id); }}
                      style={{
                        padding: '10px 14px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        outline: 'none',
                        backgroundColor: !allSelected && farm === entry.id ? 'rgba(26, 77, 58, 0.1)' : 'transparent',
                      }}
                      onMouseEnter={(event) => {
                        if (allSelected || farm !== entry.id) {
                          event.currentTarget.style.backgroundColor = 'rgba(26, 77, 58, 0.05)';
                        }
                      }}
                      onMouseLeave={(event) => {
                        if (allSelected || farm !== entry.id) {
                          event.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <GiFarmTractor style={{ color: '#1A4D3A' }} />
                        <span style={{ color: '#1A4D3A', fontWeight: !allSelected && farm === entry.id ? 600 : 400 }}>
                          {entry.name}
                        </span>
                      </div>
                    </DropdownMenu.Item>
                  ))
                ) : (
                  <DropdownMenu.Item disabled style={{ padding: '10px 14px', color: '#666', fontStyle: 'italic' }}>
                    {farmsLoading ? 'Carregando fazendas…' : farmsError ? 'Não foi possível carregar as fazendas' : 'Nenhuma fazenda disponível'}
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </SidebarHeader>

        <SidebarContent>
          {!isVisuallyCollapsed && <AreaSwitch placement="sidebar" />}
          {groupedItems.map(({ section, items }) => {
            return (
              <NavSection key={section.key}>
                {!isVisuallyCollapsed && <SectionTitle>{section.label}</SectionTitle>}
                {items.map((item) => {
                  const active = isNavigationItemActive(item, location.pathname);

                  return (
                    <NavItem
                      key={item.key}
                      onClick={() => { handleNavClick(item.route); }}
                      data-active={active}
                      data-collapsed={isVisuallyCollapsed}
                      aria-label={item.label}
                      aria-current={active ? 'page' : undefined}
                      title={isVisuallyCollapsed ? item.label : undefined}
                    >
                      <NavItemIcon data-active={active}>{createElement(item.icon)}</NavItemIcon>
                      {!isVisuallyCollapsed && <NavItemLabel>{item.label}</NavItemLabel>}
                    </NavItem>
                  );
                })}
              </NavSection>
            );
          })}
        </SidebarContent>

        <SidebarFooter>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <NavItem data-collapsed={isVisuallyCollapsed} style={{ marginBottom: '8px' }} as="button">
                <NavItemIcon>
                  <PeopleOutlineOutlined />
                </NavItemIcon>
                {!isVisuallyCollapsed && <NavItemLabel>Perfil</NavItemLabel>}
              </NavItem>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                side="right"
                sideOffset={8}
                style={{
                  minWidth: '220px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8faf9 100%)',
                  border: '1px solid rgba(45, 106, 79, 0.16)',
                  borderRadius: '14px',
                  padding: '8px',
                  boxShadow: '0 18px 35px rgba(15, 61, 44, 0.22)',
                  zIndex: 1302,
                }}
              >
                <DropdownMenu.Item
                  onSelect={() => { setProfileCarregado(true); setIsProfileModalOpen(true); }}
                  style={{
                    padding: '11px 12px',
                    minHeight: '42px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    color: '#1f4737',
                    fontWeight: 600,
                  }}
                >
                  Editar Perfil
                  <FiEdit />
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={() => { setSenhaCarregada(true); setIsPasswordModalOpen(true); }}
                  style={{
                    padding: '11px 12px',
                    minHeight: '42px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    color: '#1f4737',
                    fontWeight: 600,
                  }}
                >
                  Alterar Senha
                  <FiLock />
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={handleSignOut}
                  style={{
                    padding: '11px 12px',
                    minHeight: '42px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: '#1f4737',
                    fontWeight: 600,
                  }}
                >
                  Sair
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>

          <Suspense fallback={null}>
            {profileCarregado && (
              <MyProfileModal modal={isProfileModalOpen} setModal={setIsProfileModalOpen} renderTrigger={false} />
            )}
            {senhaCarregada && (
              <PasswordModal modal={isPasswordModalOpen} setModal={setIsPasswordModalOpen} renderTrigger={false} />
            )}
          </Suspense>

          <CollapseButton
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
            data-collapsed={isCollapsed}
          >
            {isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </CollapseButton>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
}
